import { inject, Injectable } from '@angular/core';
import { ApiService, UtilsService } from 'shared-lib';
import { catchError, EMPTY, from, Observable, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { LocalStorageService } from '../storage/local-storage.service';
import { LoginResponse, OtpVerificationResponse } from './auth.type';
import { environment } from 'projects/self-care/src/environments/environment';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { LOCAL_STORAGE_KEYS, LOGIN_STORAGE_KEY, SESSION_STORAGE_KEY } from '../storage/storage.constant';
import { Router } from '@angular/router';
import { UserService } from '../user/user.service';
import { CONFIG } from '../../shared/constants/config';
import { DEFAULT_LANGUAGE } from '../../custom.config';
import { SharedDataService } from '@selfCareShared/services/shared-data.service';
import { PushNotificationService } from '@selfCareShared/services/push-notification.service';
import { take } from 'rxjs/operators';

export type LoginResult = { success: true; data: LoginResponse } | { success: false; error: unknown };

export type RegistrationInfo = {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  otp?: string;
  otpToken?: string;
  password?: string;
  otpSessionId?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly sharedDataService = inject(SharedDataService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly utilsService = inject(UtilsService);
  private readonly httpBackend = inject(HttpBackend);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly pushNotificationService = inject(PushNotificationService);

  private readonly loginResultSubject = new Subject<LoginResult>();
  readonly loginResult$ = this.loginResultSubject.asObservable();

  /** Prevents re-entrant logout when unsubscribe/logout APIs return 401 during forced logout. */
  private isLoggingOut = false;

  login(payload: { username: string; password: string }): Observable<LoginResult> {
    return of(payload).pipe(
      switchMap((credentials) =>
        this.apiService.post<LoginResponse>('auth/login', credentials).pipe(
          tap((response) => {
            this.localStorageService.setItem(LOGIN_STORAGE_KEY, JSON.stringify(response));
            this.setSession(response);
          }),
          switchMap((response) => {
            // Fetch user profile after successful login
            return this.userService.fetchUserProfile().pipe(
              switchMap(() => this.refreshTokensAfterUserProfileLoaded()),
              switchMap(() => {
                const result: LoginResult = { success: true, data: response };
                this.loginResultSubject.next(result);
                this.localStorageService.setItemObject(LOCAL_STORAGE_KEYS.PARENT, response.parent);
                this.pushNotificationService.initializePushNotifications();
                return of(result);
              }),
              catchError(() => {
                // Even if profile fetch fails, login was successful
                const result: LoginResult = { success: true, data: response };
                this.loginResultSubject.next(result);
                this.pushNotificationService.initializePushNotifications();
                return of(result);
              })
            );
          }),
          catchError((error) => {
            const result: LoginResult = { success: false, error };
            this.loginResultSubject.next(result);
            return throwError(() => error);
          })
        )
      )
    );
  }
  switchAccount(sid: string): Observable<LoginResult> {
    return of(sid).pipe(
      switchMap((credentials) =>
        this.apiService.post<LoginResponse>(`auth/switch-account/${sid}`, credentials).pipe(
          tap((response) => {
            this.localStorageService.setItem(LOGIN_STORAGE_KEY, JSON.stringify(response));
            this.setSession(response);
          }),
          switchMap((response) => {
            // Fetch user profile after successful login
            return this.userService.fetchUserProfile().pipe(
              switchMap(() => this.refreshTokensAfterUserProfileLoaded()),
              switchMap(() => {
                const result: LoginResult = { success: true, data: response };
                this.loginResultSubject.next(result);
                this.pushNotificationService.initializePushNotifications({ accountSwitch: true });
                return of(result);
              }),
              catchError(() => {
                // Even if profile fetch fails, login was successful
                const result: LoginResult = { success: true, data: response };
                this.loginResultSubject.next(result);
                this.pushNotificationService.initializePushNotifications({ accountSwitch: true });
                return of(result);
              })
            );
          }),
          catchError((error) => {
            const result: LoginResult = { success: false, error };
            this.loginResultSubject.next(result);
            return throwError(() => error);
          })
        )
      )
    );
  }

  signUp(payload: RegistrationInfo): Observable<any> {
    delete payload.otpSessionId;
    return this.apiService.post('auth/registration', payload).pipe(
      tap((response) => {
        this.localStorageService.setItem(LOGIN_STORAGE_KEY, JSON.stringify(response));
        this.setSession(response);
      }),
      switchMap((response) => {
        // Fetch user profile after successful signup
        return this.userService.fetchUserProfile().pipe(
          switchMap(() => this.refreshTokensAfterUserProfileLoaded()),
          switchMap(() => {
            const result: LoginResult = { success: true, data: response };
            this.loginResultSubject.next(result);
            this.pushNotificationService.initializePushNotifications();
            return of(result);
          }),
          catchError(() => {
            // Even if profile fetch fails, signup was successful
            const result: LoginResult = { success: true, data: response };
            this.loginResultSubject.next(result);
            this.pushNotificationService.initializePushNotifications();
            return of(result);
          })
        );
      }),
      catchError((error) => {
        const result: LoginResult = { success: false, error };
        this.loginResultSubject.next(result);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    from(this.pushNotificationService.unsubscribeFromPush())
      .pipe(
        catchError(() => of(undefined)),
        switchMap(() => this.apiService.delete('v1/superapp/profile/logout').pipe(take(1))),
        take(1)
      )
      .subscribe({
        next: () => {
          this.finishLocalLogout();
        },
        error: () => {
          this.finishLocalLogout();
        },
      });
  }

  inAppLogout(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    // Best-effort server unsubscribe (token may already be invalid), then always clear locally.
    void this.pushNotificationService.unsubscribeFromPush().finally(() => {
      this.finishLocalLogout();
    });
  }

  /**
   * Unsubscribes all web-push rows (logoutAll payload), then hits profile logout-all, then clears local session.
   * Callers should not chain a second logout() — this finishes the local session itself.
   */
  logoutAll(): Observable<unknown> {
    if (this.isLoggingOut) {
      return of(undefined);
    }
    this.isLoggingOut = true;

    const linkedAccounts = this.sharedDataService.linkedAccounts()?.map((acc) => acc.id) || [];

    return from(this.pushNotificationService.unsubscribeFromPush({ logoutAll: true })).pipe(
      catchError(() => of(undefined)),
      switchMap(() => this.apiService.deleteWithBody('v1/superapp/profile/logout-all', linkedAccounts)),
      tap({
        next: () => this.finishLocalLogout(),
        error: () => {
          this.isLoggingOut = false;
        },
      })
    );
  }

  private finishLocalLogout(): void {
    this.loginResultSubject.next({ success: false, error: 'User logged out' });
    this.userService.clearUser();
    // Language and feature visibility are not session-specific; clearing them hides the
    // language selector until a full reload and drops Accept-Language on login APIs.
    this.localStorageService.clearExcept([
      LOCAL_STORAGE_KEYS.IS_ONBOARDING_DONE,
      LOCAL_STORAGE_KEYS.CURRENT_LANGUAGE,
      LOCAL_STORAGE_KEYS.FEATURE_VISIBILITY,
    ]);
    this.sharedDataService.clearSessionData();
    this.router.navigateByUrl('/auth/login');
    this.isLoggingOut = false;
  }

  /**
   * After user profile is loaded (login / switch / signup / post-payment), exchange the
   * current refresh token for a new pair so stored tokens stay in sync with the server
   * (same `/api/auth/refresh` flow as the auth error interceptor).
   */
  refreshTokensAfterUserProfileLoaded(): Observable<void> {
    const refreshToken = this.localStorageService.getItem(SESSION_STORAGE_KEY.REFRESH_TOKEN);
    if (!refreshToken || refreshToken === 'undefined') {
      return of(undefined);
    }
    return this.sendTokenRefreshRequest(refreshToken).pipe(
      switchMap((res: unknown) => (this.isTokenRefreshPayload(res) ? of(undefined) : EMPTY))
    );
  }

  sendTokenRefreshRequest(refresh_token: string): Observable<any> {
    const requestUrl = `${environment.baseUrl}/api/auth/refresh`;
    const httpClient = new HttpClient(this.httpBackend);
    const selectedLanguage = this.localStorageService.getItem(LOCAL_STORAGE_KEYS.CURRENT_LANGUAGE)?.toUpperCase();
    const acceptLanguage = selectedLanguage === 'BN' ? 'BN' : DEFAULT_LANGUAGE;

    const body = {
      parent: true,
      grantType: 'refresh_token',
      refresh_token: refresh_token,
      login_type_credential: true,
    };
    return httpClient.post(requestUrl, body, { headers: { 'Accept-Language': acceptLanguage } }).pipe(
      tap((tokens) => {
        this.setSession(tokens);
      }),
      catchError((error) => {
        console.log({ 'Auth isRefreshing': error });
        this.logout();
        this.router.navigateByUrl('/auth/login').then();
        return of(error);
      })
    );
  }

  setSession(tokens: any): void {
    // this.localStorageService.setItem(this.LOGIN_STORAGE_KEY, JSON.stringify(tokens));
    this.localStorageService.setItem(SESSION_STORAGE_KEY.ACCESS_TOKEN, tokens.access_token);
    this.localStorageService.setItem(SESSION_STORAGE_KEY.REFRESH_TOKEN, tokens.refresh_token);
    // this.localStorageService.setItem(this.SESSION_STORAGE_KEY.USER_ID, tokens.user_id);
  }

  getStoredLoginResponse(): LoginResponse | null {
    const stored = this.localStorageService.getItem(LOGIN_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  clearStoredLoginResponse(): void {
    this.localStorageService.removeItem(LOGIN_STORAGE_KEY);
  }

  private isTokenRefreshPayload(res: unknown): res is { access_token: string; refresh_token: string } {
    return (
      !!res &&
      typeof res === 'object' &&
      'access_token' in res &&
      typeof (res as { access_token?: unknown }).access_token === 'string' &&
      'refresh_token' in res &&
      typeof (res as { refresh_token?: unknown }).refresh_token === 'string'
    );
  }

  passwordRules(): Observable<any> {
    return this.apiService.get('v1/superapp/default-password/get-current');
  }

  validatePhone(phoneNumber: string): Observable<boolean> {
    return this.apiService.get('auth/validate-phone', { phoneNumber });
  }
  forgetUser(phoneOrSid: string): Observable<OtpVerificationResponse> {
    const payload = {
      eventCode: CONFIG.EVENT_CODE,
      phoneNumberOrCustomerCode: phoneOrSid,
      deviceId: CONFIG.DEVICE_ID,
    };
    return this.apiService.post<OtpVerificationResponse>(`auth/forgot-user`, payload);
  }
}
